import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, MailCheck } from "lucide-react";
import { Logo, LogoMark } from "@/components/logo";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BRAND } from "@/lib/brand";
import { cn } from "@/lib/utils";

const RESEND_DELAY_MS = 30_000;

export function Login() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [error, setError] = useState<string | null>(null);
  const [canResendAt, setCanResendAt] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());
  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    if (!canResendAt || now >= canResendAt) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [canResendAt, now]);

  async function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault();
    setError(null);
    if (status === "sent") {
      setIsResending(true);
    } else {
      setStatus("sending");
    }
    try {
      const res = await fetch("/api/auth/email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) throw new Error("Request failed");
      setCanResendAt(Date.now() + RESEND_DELAY_MS);
      setNow(Date.now());
      setStatus("sent");
      setError(null);
    } catch {
      setStatus("error");
      setError("We couldn't send the email. Please try again.");
    } finally {
      setIsResending(false);
    }
  }

  const secondsUntilResend = canResendAt ? Math.max(0, Math.ceil((canResendAt - now) / 1000)) : 0;
  const canResend = !canResendAt || now >= canResendAt;

  return (
    <div className="flex min-h-screen flex-col bg-zinc-50 px-4 text-zinc-900">
      <div className="mx-auto flex w-full max-w-5xl items-center justify-between px-4 py-5 sm:px-6 lg:px-8">
        <Link
          to="/"
          className="rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-zinc-400/60"
        >
          <Logo showWordmark />
        </Link>
      </div>

      <main className="flex flex-1 items-center justify-center pb-12">
        <Card className="w-full max-w-sm border-zinc-200 bg-white shadow-sm">
          {status === "sent" ? (
            <CardContent className="pt-8 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50">
                <MailCheck size={24} className="text-emerald-600" />
              </div>
              <h2 className="mt-4 text-xl font-semibold tracking-tight">Check your email</h2>
              <p className="mt-2 text-sm text-zinc-600">
                We sent a sign-in link to <span className="font-medium text-zinc-900">{email}</span>
                . It expires in 15 minutes.
              </p>
              <Button
                type="button"
                variant="secondary"
                className="mt-6 w-full"
                disabled={!canResend || isResending}
                onClick={() => void handleSubmit()}
                aria-busy={isResending}
              >
                {isResending ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Sending…
                  </>
                ) : canResend ? (
                  "Resend email"
                ) : (
                  `Resend email in ${secondsUntilResend}s`
                )}
              </Button>
              <Button
                variant="ghost"
                className="mt-2 w-full"
                onClick={() => {
                  setEmail("");
                  setStatus("idle");
                  setError(null);
                  setCanResendAt(null);
                }}
              >
                Use a different email
              </Button>
            </CardContent>
          ) : (
            <>
              <CardHeader className="text-center">
                <div className="mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-zinc-100">
                  <LogoMark size={22} className="text-zinc-900" />
                </div>
                <CardTitle className="text-2xl font-semibold tracking-tight">
                  Let's get you set up
                </CardTitle>
                <CardDescription>
                  Enter your email and we'll send you a sign-in link. No password needed.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="h-10"
                    />
                  </div>
                  {error && (
                    <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                      {error}
                    </div>
                  )}
                  <Button
                    type="submit"
                    disabled={status === "sending"}
                    className="w-full bg-zinc-900 text-zinc-50 hover:bg-zinc-800"
                    aria-busy={status === "sending"}
                  >
                    {status === "sending" ? (
                      <>
                        <Loader2 size={16} className="mr-2 animate-spin" />
                        Sending…
                      </>
                    ) : (
                      "Continue with email"
                    )}
                  </Button>
                </form>
                <p className="mt-5 text-center text-xs text-zinc-500">
                  New here? We'll create your account automatically.
                </p>
              </CardContent>
            </>
          )}
        </Card>
      </main>

      <div className="pb-6 text-center">
        <Link
          to="/"
          className={cn(
            buttonVariants({ variant: "ghost", size: "sm" }),
            "inline-flex items-center gap-2 text-zinc-500 hover:text-zinc-800"
          )}
        >
          <ArrowLeft size={14} />
          Back to {BRAND.name}
        </Link>
      </div>
    </div>
  );
}
